import {
  TrendingUp,
  Droplets,
  Sparkles,
  Pill,
  Dumbbell,
  Ruler,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { gluceraWheelCaption } from "../../utils/gluceraTypography";

interface HomeFoodActionSheetProps {
  open: boolean;
  onClose: () => void;
}

const QUICK_ACTIONS = [
  {
    icon: TrendingUp,
    label: "Weight\nManagement",
    circleBg:
      "bg-sky-100 shadow-sm ring-1 ring-sky-200/70 dark:bg-sky-950/40 dark:ring-sky-800/40",
    iconClass: "text-sky-600 dark:text-sky-400",
    route: GluceraNavigationRoutes.WEIGHT_MANAGEMENT_DETAIL,
  },
  {
    icon: Droplets,
    label: "Glucose\nReadings",
    circleBg:
      "bg-emerald-100 shadow-sm ring-1 ring-emerald-200/70 dark:bg-emerald-950/40 dark:ring-emerald-800/40",
    iconClass: "text-emerald-600 dark:text-emerald-400",
    route: GluceraNavigationRoutes.GLUCOSE_READINGS,
  },
  {
    icon: Sparkles,
    label: "Health\nInsights",
    circleBg:
      "bg-violet-100 shadow-sm ring-1 ring-violet-200/70 dark:bg-violet-950/40 dark:ring-violet-800/40",
    iconClass: "text-violet-600 dark:text-violet-400",
    route: GluceraNavigationRoutes.HEALTH_INSIGHTS,
  },
  {
    icon: Pill,
    label: "Medication",
    circleBg:
      "bg-orange-100 shadow-sm ring-1 ring-orange-200/70 dark:bg-orange-950/40 dark:ring-orange-800/40",
    iconClass: "text-orange-600 dark:text-orange-400",
    route: GluceraNavigationRoutes.MEDICATIONS,
  },
  {
    icon: Dumbbell,
    label: "Exercise",
    circleBg:
      "bg-indigo-100 shadow-sm ring-1 ring-indigo-200/70 dark:bg-indigo-950/40 dark:ring-indigo-800/40",
    iconClass: "text-indigo-600 dark:text-indigo-400",
    route: GluceraNavigationRoutes.SUGGESTED_WORKOUTS,
  },
  {
    icon: Ruler,
    label: "Measure",
    circleBg:
      "bg-amber-100 shadow-sm ring-1 ring-amber-300/75 dark:bg-amber-950/40 dark:ring-amber-800/45",
    iconClass: "text-amber-950 dark:text-amber-300",
    route: GluceraNavigationRoutes.CUSTOM_SCREEN,
  },
] as const;

const HomeFoodActionSheet = ({ open, onClose }: HomeFoodActionSheetProps) => {
  const navigate = useNavigate();

  if (!open) return null;

  const go = (route: string) => {
    onClose();
    navigate(`/glucera${route}`);
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col justify-end">
      <div
        className="absolute inset-0 bg-black/35 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="rounded-t-3xl bg-zinc-50 shadow-2xl ring-1 ring-zinc-200/80 dark:bg-zinc-900 dark:ring-zinc-700/50">
          <div className="grid grid-cols-3 items-center gap-2 px-4 pb-1 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center justify-self-start rounded-full bg-white text-zinc-500 shadow-sm ring-1 ring-zinc-200/80 transition-colors hover:bg-zinc-100 active:scale-95 dark:bg-zinc-800 dark:text-zinc-400 dark:ring-zinc-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex justify-center">
              <div className="h-1 w-10 rounded-full bg-zinc-200 dark:bg-zinc-600" />
            </div>
            <span className="pointer-events-none select-none" aria-hidden />
          </div>

          <div className="px-5 pb-[max(1.5rem,env(safe-area-inset-bottom,1.5rem))] pt-2">
            <div className="mb-5 grid grid-cols-3 gap-3">
              {QUICK_ACTIONS.slice(0, 3).map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    type="button"
                    key={item.label}
                    onClick={() => go(item.route)}
                    className="flex flex-col items-center gap-2 transition-transform duration-150 active:scale-95"
                  >
                    <div
                      className={`flex h-[68px] w-[68px] items-center justify-center rounded-full ${item.circleBg}`}
                    >
                      <Icon className={`h-7 w-7 ${item.iconClass}`} strokeWidth={2} />
                    </div>
                    <span className={gluceraWheelCaption}>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-3">
              {QUICK_ACTIONS.slice(3).map(({ icon: Icon, label, circleBg, iconClass, route }) => (
                <button
                  type="button"
                  key={label}
                  onClick={() => go(route)}
                  className="flex flex-col items-center gap-2 transition-transform duration-150 active:scale-95"
                >
                  <div
                    className={`flex h-[68px] w-[68px] items-center justify-center rounded-full ${circleBg}`}
                  >
                    <Icon className={`h-7 w-7 ${iconClass}`} strokeWidth={2} />
                  </div>
                  <span className={gluceraWheelCaption}>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeFoodActionSheet;
